import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { getNotifications, markNotificationRead, type NotificationItem } from '../api/notifications';
import { subscribePush } from '../api/push';

interface NotificationContextType {
  notifications: NotificationItem[];
  currentNotification: NotificationItem | null;
  dismissNotification: () => void;
  refreshNotifications: () => void;
  notificationPermission: NotificationPermission | 'unsupported';
  requestPermission: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { loginKey } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [currentNotification, setCurrentNotification] = useState<NotificationItem | null>(null);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>(
    'Notification' in window ? Notification.permission : 'unsupported',
  );

  const refreshNotifications = useCallback(async () => {
    if (!loginKey) return;

    const response = await getNotifications(loginKey);

    if (response.ok && response.data?.error === null && response.data.notifications) {
      const items = response.data.notifications;
      setNotifications(items);
      if (items.length > 0 && !currentNotification) {
        setCurrentNotification(items[0]);
      }
    }
  }, [loginKey, currentNotification]);

  const dismissNotification = useCallback(async () => {
    if (!currentNotification || !loginKey) return;

    markNotificationRead(loginKey, currentNotification.id);

    setNotifications((prev) => {
      const remaining = prev.filter((n) => n.id !== currentNotification.id);

      if (remaining.length > 0) {
        setCurrentNotification(remaining[0]);
      } else {
        setCurrentNotification(null);
      }

      return remaining;
    });
  }, [currentNotification, loginKey]);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      setNotificationPermission('unsupported');
      return;
    }

    const result = await Notification.requestPermission();
    setNotificationPermission(result);
  }, []);

  useEffect(() => {
    if (!loginKey) return;

    refreshNotifications();

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((result) => {
        setNotificationPermission(result);
        if (result === 'granted') {
          subscribePush(loginKey).catch((e) => console.error('Push subscription failed:', e));
        }
      });
    } else if ('Notification' in window && Notification.permission === 'granted') {
      subscribePush(loginKey).catch((e) => console.error('Push subscription failed:', e));
    }
  }, [loginKey]);

  const value: NotificationContextType = {
    notifications,
    currentNotification,
    dismissNotification,
    refreshNotifications,
    notificationPermission,
    requestPermission,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within a NotificationProvider');
  return context;
}

export default NotificationContext;
